import { useMemo, useState } from "react";
import StatusBadge from "@/components/base/StatusBadge";
import Pagination from "@/components/base/Pagination";
import { Skeleton } from "@/components/base/Loading";
import { usePagination } from "@/hooks/usePagination";
import { formatFullDate, daysUntil } from "@/lib/format";
import { LICENSE_STATUS_META, editionLabel } from "@/lib/license";
import BatchRenewConfirmModal from "@/pages/admin/components/BatchRenewConfirmModal";
import type { AdminLicense, BatchRenewResult, RenewInput } from "@/pages/admin/hooks/useAdminData";

type Filter = "all" | "active" | "expiring" | "revoked" | "expired";

const EXPIRING_WINDOW_DAYS = 30;
const WINDOW_OPTIONS = [15, 30, 60];
const PAGE_SIZE = 8;

interface LicenseManagerProps {
  licenses: AdminLicense[];
  loading: boolean;
  onRevoke: (id: number) => Promise<void>;
  onRestore: (id: number) => Promise<void>;
  onRenew: (id: number, input: RenewInput) => Promise<{ expiresAt: string }>;
  onRenewMany: (ids: number[], expiresMonths: number) => Promise<BatchRenewResult>;
  onSetOpenLimit: (id: number, maxOpens: number | null, resetCount?: boolean) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
  initialFilter?: Filter;
  initialWindow?: number;
}

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "active", label: "有效" },
  { key: "expiring", label: "即将到期" },
  { key: "expired", label: "已过期" },
  { key: "revoked", label: "已吊销" },
];

const EXTEND_OPTIONS = [12, 24, 36];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isExpiringSoon(license: AdminLicense, windowDays: number): boolean {
  if (license.status !== "active" || !license.expires_at) return false;
  const days = daysUntil(license.expires_at);
  return days >= 0 && days <= windowDays;
}

export default function LicenseManager({
  licenses,
  loading,
  onRevoke,
  onRestore,
  onRenew,
  onRenewMany,
  onSetOpenLimit,
  notify,
  initialFilter = "all",
  initialWindow = EXPIRING_WINDOW_DAYS,
}: LicenseManagerProps) {
  const [filter, setFilter] = useState<Filter>(initialFilter);
  const [windowDays, setWindowDays] = useState<number>(initialWindow);
  const [keyword, setKeyword] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [renewId, setRenewId] = useState<number | null>(null);
  const [renewMode, setRenewMode] = useState<"extend" | "set">("extend");
  const [renewMonths, setRenewMonths] = useState(12);
  const [renewDate, setRenewDate] = useState("");
  const [renewBusy, setRenewBusy] = useState(false);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchRenew, setBatchRenew] = useState(false);
  const [batchMonths, setBatchMonths] = useState(12);
  const [batchBusy, setBatchBusy] = useState(false);
  const [confirmBatchOpen, setConfirmBatchOpen] = useState(false);

  const [limitId, setLimitId] = useState<number | null>(null);
  const [limitValue, setLimitValue] = useState("");
  const [limitReset, setLimitReset] = useState(false);
  const [limitBusy, setLimitBusy] = useState(false);

  const matchesFilter = (license: AdminLicense, key: Filter): boolean => {
    if (key === "all") return true;
    if (key === "expiring") return isExpiringSoon(license, windowDays);
    return license.status === key;
  };

  const countFor = (key: Filter): number => licenses.filter((license) => matchesFilter(license, key)).length;

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return licenses.filter((license) => {
      if (!matchesFilter(license, filter)) return false;
      if (!term) return true;
      return (
        license.product_name.toLowerCase().includes(term) ||
        license.license_key.toLowerCase().includes(term) ||
        (license.user_id ?? "").toLowerCase().includes(term)
      );
    });
  }, [licenses, filter, keyword, windowDays]);

  const { page, totalPages, total, pageSize, pagedItems, setPage } = usePagination(
    filtered,
    PAGE_SIZE,
    `${filter}|${keyword}|${windowDays}`,
  );

  const selectable = useMemo(() => pagedItems.filter((license) => license.status !== "revoked"), [pagedItems]);
  const allSelectableChecked = selectable.length > 0 && selectable.every((item) => selected.has(item.id));
  const selectedLicenses = useMemo(() => licenses.filter((item) => selected.has(item.id)), [licenses, selected]);

  const resetSelection = () => {
    setSelected(new Set());
    setBatchRenew(false);
    setConfirmBatchOpen(false);
  };

  const changeFilter = (next: Filter) => {
    setFilter(next);
    resetSelection();
  };

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectableChecked) {
        selectable.forEach((item) => next.delete(item.id));
      } else {
        selectable.forEach((item) => next.add(item.id));
      }
      return next;
    });
  };

  const openRenew = (license: AdminLicense) => {
    setLimitId(null);
    setRenewId(license.id);
    setRenewMode("extend");
    setRenewMonths(12);
    setRenewDate(license.expires_at ? license.expires_at.slice(0, 10) : todayIso());
  };

  const closeRenew = () => {
    setRenewId(null);
    setRenewBusy(false);
  };

  const run = async (id: number, action: "revoke" | "restore", product: string) => {
    setBusyId(id);
    try {
      if (action === "revoke") {
        await onRevoke(id);
        notify(`已吊销「${product}」的许可证`, "success");
      } else {
        await onRestore(id);
        notify(`已恢复「${product}」的许可证`, "success");
      }
    } catch (err) {
      notify(err instanceof Error ? err.message : "操作失败", "error");
    } finally {
      setBusyId(null);
    }
  };

  const submitRenew = async (license: AdminLicense) => {
    if (renewMode === "set" && !renewDate) {
      notify("请选择新的到期日期", "error");
      return;
    }
    setRenewBusy(true);
    try {
      const input: RenewInput =
        renewMode === "extend" ? { expiresMonths: renewMonths } : { expiresAt: `${renewDate}T23:59:59` };
      const result = await onRenew(license.id, input);
      notify(
        renewMode === "extend"
          ? `已将「${license.product_name}」续期 ${renewMonths} 个月，到期至 ${formatFullDate(result.expiresAt)}`
          : `已将「${license.product_name}」到期时间调整为 ${formatFullDate(result.expiresAt)}`,
        "success",
      );
      closeRenew();
    } catch (err) {
      notify(err instanceof Error ? err.message : "续期失败", "error");
      setRenewBusy(false);
    }
  };

  const submitBatchRenew = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBatchBusy(true);
    try {
      const result = await onRenewMany(ids, batchMonths);
      const parts = [`已批量续期 ${result.processed} 张许可证（+${batchMonths} 个月）`];
      if (result.skipped > 0) parts.push(`跳过 ${result.skipped} 张`);
      notify(parts.join("，"), "success");
      resetSelection();
    } catch (err) {
      notify(err instanceof Error ? err.message : "批量续期失败", "error");
    } finally {
      setBatchBusy(false);
      setConfirmBatchOpen(false);
    }
  };

  const openLimit = (license: AdminLicense) => {
    setRenewId(null);
    setLimitId(license.id);
    setLimitValue(license.max_opens != null ? String(license.max_opens) : "");
    setLimitReset(false);
  };

  const closeLimit = () => {
    setLimitId(null);
    setLimitBusy(false);
  };

  const submitLimit = async (license: AdminLicense) => {
    const trimmed = limitValue.trim();
    let parsed: number | null = null;
    if (trimmed !== "") {
      const num = Number(trimmed);
      if (!Number.isFinite(num) || num < 0) {
        notify("打开次数上限需为不小于 0 的整数", "error");
        return;
      }
      parsed = num > 0 ? Math.floor(num) : null;
    }
    setLimitBusy(true);
    try {
      await onSetOpenLimit(license.id, parsed, limitReset);
      notify(
        parsed === null
          ? `已取消「${license.product_name}」的打开次数限制`
          : `已将「${license.product_name}」打开次数上限设为 ${parsed} 次${limitReset ? "（并重置计数）" : ""}`,
        "success",
      );
      closeLimit();
    } catch (err) {
      notify(err instanceof Error ? err.message : "保存失败", "error");
      setLimitBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {[0, 1, 2].map((item) => (
          <div key={item} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="mt-4 h-4 w-2/3" />
            <Skeleton className="mt-4 h-9 w-32" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line" />
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索产品、密钥或用户 ID"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword("")}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-100"
              aria-label="清除搜索"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((item) => {
            const active = item.key === filter;
            const isExpiring = item.key === "expiring";
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => changeFilter(item.key)}
                className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : isExpiring
                      ? "border border-accent-300 bg-accent-50 text-accent-800 hover:bg-accent-100"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                {isExpiring && (
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-alarm-warning-line" />
                  </span>
                )}
                {item.label}
                <span className={active ? "text-background-200" : "text-foreground-400"}>{countFor(item.key)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {filter === "expiring" && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5">
          <span className="flex items-center gap-1.5 text-xs font-medium text-accent-800">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-alarm-warning-line" />
            </span>
            到期窗口
          </span>
          <div className="inline-flex rounded-full border border-background-300 bg-background-50 p-1">
            {WINDOW_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setWindowDays(option)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                  windowDays === option
                    ? "bg-primary-500 font-medium text-background-50"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {option} 天
              </button>
            ))}
          </div>
          <span className="text-[11px] text-foreground-500">
            {windowDays} 天内到期的有效许可证，共 {countFor("expiring")} 张
          </span>
        </div>
      )}

      {selectable.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-foreground-800">
            <input
              type="checkbox"
              checked={allSelectableChecked}
              onChange={toggleSelectAll}
              className="h-4 w-4 cursor-pointer accent-primary-600"
            />
            全选本页可续期（{selectable.length}）
            {selected.size > 0 && (
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700">
                已选 {selected.size}
              </span>
            )}
          </label>
          <button
            type="button"
            disabled={selected.size === 0}
            onClick={() => setBatchRenew((prev) => !prev)}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              batchRenew
                ? "bg-primary-600 text-background-50"
                : "border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100"
            }`}
          >
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-calendar-check-line" />
            </span>
            批量续期
          </button>
        </div>
      )}

      {batchRenew && selected.size > 0 && (
        <div className="mb-4 rounded-md border border-primary-200 bg-primary-50 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
            <span className="flex h-4 w-4 items-center justify-center text-primary-700">
              <i className="ri-calendar-check-line" />
            </span>
            批量续期 {selected.size} 张许可证
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {EXTEND_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setBatchMonths(option)}
                className={`cursor-pointer whitespace-nowrap rounded-md border px-4 py-1.5 text-sm transition-colors ${
                  batchMonths === option
                    ? "border-primary-400 bg-primary-100 font-medium text-primary-700"
                    : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                }`}
              >
                +{option} 个月
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
            在每张许可证当前到期时间基础上顺延；已过期的从今天起算。已吊销的许可证会被自动跳过。
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={batchBusy}
              onClick={() => setConfirmBatchOpen(true)}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-check-line" />
              </span>
              {`核对并提交（${selected.size}）`}
            </button>
            <button
              type="button"
              onClick={() => setBatchRenew(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
            <i className="ri-key-2-line text-2xl" />
          </span>
          <p className="text-sm font-medium text-foreground-800">没有匹配的许可证</p>
          <p className="max-w-sm text-xs text-foreground-500">调整搜索关键词或筛选条件再试试。</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200">
          <div className="hidden bg-background-100 px-5 py-3 text-xs font-medium text-foreground-600 lg:grid lg:grid-cols-12 lg:gap-3">
            <span className="col-span-4">产品 / 密钥</span>
            <span className="col-span-2">状态</span>
            <span className="col-span-2">席位 / 激活</span>
            <span className="col-span-2">到期时间</span>
            <span className="col-span-2 text-right">操作</span>
          </div>
          {pagedItems.map((license) => {
            const meta =
              LICENSE_STATUS_META[license.status] ?? {
                label: license.status,
                className: "bg-background-200 text-foreground-700",
              };
            const days = license.expires_at ? daysUntil(license.expires_at) : null;
            const lastFour = license.license_key.slice(-4);
            const canRevoke = license.status !== "revoked";
            const expired = days !== null && days < 0;
            const expiring = isExpiringSoon(license, windowDays);
            const openPanel = renewId === license.id;
            const selectableRow = license.status !== "revoked";
            const checked = selected.has(license.id);

            return (
              <div
                key={license.id}
                className={`border-t border-background-200 ${checked ? "bg-primary-50/60" : "bg-background-50"}`}
              >
                <div className="grid gap-3 px-5 py-4 lg:grid-cols-12 lg:items-center">
                  <div className="lg:col-span-4">
                    <div className="flex items-start gap-3">
                      {selectableRow ? (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelect(license.id)}
                          aria-label={`选择 ${license.product_name} 的许可证`}
                          className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary-600"
                        />
                      ) : (
                        <span className="mt-1 h-4 w-4 shrink-0" aria-hidden="true" />
                      )}
                      <div>
                        <p className="font-heading text-sm font-semibold text-foreground-950">{license.product_name}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                          <span className="rounded-md bg-background-100 px-2 py-0.5 text-foreground-700">
                            {editionLabel(license.edition)}
                          </span>
                          <span className="font-mono">LH-••••-{lastFour}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <StatusBadge meta={meta} />
                  </div>
                  <div className="lg:col-span-2">
                    <p className="text-sm text-foreground-900">
                      {license.activation_count} / {license.max_activations}
                    </p>
                    <p className="text-[11px] text-foreground-500">{license.seat_count} 授权席位</p>
                    <p className="text-[11px] text-foreground-500">
                      已打开 {license.open_count ?? 0} 次
                      {license.max_opens ? ` / 上限 ${license.max_opens}` : " · 不限"}
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <p className="text-sm text-foreground-900">
                      {license.expires_at ? formatFullDate(license.expires_at) : "永久"}
                    </p>
                    {expiring && days !== null && (
                      <p className="text-[11px] font-medium text-accent-700">仅剩 {days} 天</p>
                    )}
                    {expired && <p className="text-[11px] font-medium text-accent-700">已过期</p>}
                  </div>
                  <div className="flex flex-wrap gap-2 lg:col-span-2 lg:justify-end">
                    <button
                      type="button"
                      onClick={() => (limitId === license.id ? closeLimit() : openLimit(license))}
                      className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                        limitId === license.id
                          ? "border-secondary-300 bg-secondary-100 text-secondary-900"
                          : "border-background-300 bg-background-50 text-foreground-800 hover:bg-background-100"
                      }`}
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-lock-line" />
                      </span>
                      限制
                    </button>
                    <button
                      type="button"
                      onClick={() => (openPanel ? closeRenew() : openRenew(license))}
                      className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                        openPanel
                          ? "border-primary-300 bg-primary-100 text-primary-700"
                          : "border-background-300 bg-background-50 text-foreground-800 hover:bg-background-100"
                      }`}
                    >
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-calendar-check-line" />
                      </span>
                      续期
                    </button>
                    {canRevoke ? (
                      <button
                        type="button"
                        disabled={busyId === license.id}
                        onClick={() => {
                          void run(license.id, "revoke", license.product_name);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-800 transition-colors hover:bg-accent-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-forbid-2-line" />
                        </span>
                        {busyId === license.id ? "处理中…" : "吊销"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busyId === license.id}
                        onClick={() => {
                          void run(license.id, "restore", license.product_name);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-refresh-line" />
                        </span>
                        {busyId === license.id ? "处理中…" : "恢复"}
                      </button>
                    )}
                  </div>
                </div>

                {openPanel && (
                  <div className="mx-5 mb-4 rounded-md border border-primary-200 bg-primary-50 p-4">
                    <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                      <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                        <i className="ri-calendar-check-line" />
                      </span>
                      续期 / 调整到期时间 · {license.product_name}
                    </p>
                    <div className="mt-3 inline-flex rounded-full border border-background-300 bg-background-50 p-1">
                      {[
                        { key: "extend" as const, label: "按月份续期" },
                        { key: "set" as const, label: "指定到期日" },
                      ].map((mode) => (
                        <button
                          key={mode.key}
                          type="button"
                          onClick={() => setRenewMode(mode.key)}
                          className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1 text-xs transition-colors ${
                            renewMode === mode.key
                              ? "bg-background-50 font-medium text-foreground-950 shadow-sm"
                              : "text-foreground-600 hover:text-foreground-900"
                          }`}
                        >
                          {mode.label}
                        </button>
                      ))}
                    </div>

                    {renewMode === "extend" ? (
                      <div className="mt-3">
                        <div className="flex flex-wrap gap-2">
                          {EXTEND_OPTIONS.map((option) => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setRenewMonths(option)}
                              className={`cursor-pointer whitespace-nowrap rounded-md border px-4 py-1.5 text-sm transition-colors ${
                                renewMonths === option
                                  ? "border-primary-400 bg-primary-100 font-medium text-primary-700"
                                  : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                              }`}
                            >
                              +{option} 个月
                            </button>
                          ))}
                        </div>
                        <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
                          在当前到期时间基础上顺延；若许可证已过期，则从今天起算。
                        </p>
                      </div>
                    ) : (
                      <label className="mt-3 flex max-w-xs flex-col gap-1.5 text-xs text-foreground-600">
                        新的到期日期
                        <input
                          type="date"
                          value={renewDate}
                          min={todayIso()}
                          onChange={(event) => setRenewDate(event.target.value)}
                          className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                        />
                      </label>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={renewBusy}
                        onClick={() => {
                          void submitRenew(license);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-check-line" />
                        </span>
                        {renewBusy ? "处理中…" : "确认更新"}
                      </button>
                      <button
                        type="button"
                        onClick={closeRenew}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}

                {limitId === license.id && (
                  <div className="mx-5 mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-4">
                    <p className="flex items-center gap-2 text-sm font-medium text-foreground-950">
                      <span className="flex h-4 w-4 items-center justify-center text-secondary-900">
                        <i className="ri-lock-line" />
                      </span>
                      打开次数限制 · {license.product_name}
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
                      客户端每次联网校验会累计一次「打开」；到达上限后校验会被拒绝。留空或填 0 表示不限制。当前已用{" "}
                      {license.open_count ?? 0} 次{license.max_opens ? ` / 上限 ${license.max_opens} 次` : ""}。
                    </p>
                    <div className="mt-3 flex flex-wrap items-end gap-4">
                      <label className="flex flex-col gap-1.5 text-xs text-foreground-600">
                        打开次数上限
                        <input
                          type="number"
                          min={0}
                          value={limitValue}
                          onChange={(event) => setLimitValue(event.target.value)}
                          placeholder="不限"
                          className="w-40 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                        />
                      </label>
                      <label className="inline-flex cursor-pointer items-center gap-2 pb-2 text-xs text-foreground-700">
                        <input
                          type="checkbox"
                          checked={limitReset}
                          onChange={(event) => setLimitReset(event.target.checked)}
                          className="h-4 w-4 cursor-pointer accent-primary-600"
                        />
                        重置已用次数
                      </label>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={limitBusy}
                        onClick={() => {
                          void submitLimit(license);
                        }}
                        className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="flex h-4 w-4 items-center justify-center">
                          <i className="ri-check-line" />
                        </span>
                        {limitBusy ? "处理中…" : "保存限制"}
                      </button>
                      <button
                        type="button"
                        onClick={closeLimit}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onChange={setPage}
        itemLabel="张许可证"
      />

      <BatchRenewConfirmModal
        open={confirmBatchOpen}
        licenses={selectedLicenses}
        months={batchMonths}
        busy={batchBusy}
        onConfirm={() => {
          void submitBatchRenew();
        }}
        onCancel={() => setConfirmBatchOpen(false)}
      />
    </div>
  );
}